/**
 * src/lib/hooks/useChatList.js — Filtered + grouped conversation list.
 *
 * Phase 82-03 — Chat System Unification, sidebar foundation.
 *
 * Owns the sidebar's data model: conversation list, project filter, archived
 * accordion state. Groups active conversations into Today / Yesterday /
 * Past 7 / Older buckets by updated_at. Archived conversations are surfaced
 * separately so the archive accordion can collapse them.
 *
 * Side-effect contract
 * - Fetches conversations on mount and when `refreshKey` changes.
 * - Listens on `chat-conversation-updated` window event (dispatched by
 *   useChat rename/archive/restore/scope flows) to refetch.
 * - Never mutates conversations directly — refresh() is the only way to
 *   re-read after a write.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Chat scope filter (TICKET-233): conversations link to project / deal (pipeline)
// / task / person (all columns already exist — migrations 046/047/087). The
// sidebar filter used to scope by project only; scopeFilter generalizes it.
//   null            → all
//   '__unassigned__'→ no scope at all (none of the four FKs set)
//   { type, id }    → scoped to that entity
const SCOPE_FIELD_BY_TYPE = { project: 'project_id', deal: 'deal_id', task: 'task_id', person: 'assigned_person_id' };

export function matchesScope(c, scopeFilter) {
  if (!scopeFilter) return true;
  if (scopeFilter === '__unassigned__') {
    return !c.project_id && !c.deal_id && !c.task_id && !c.assigned_person_id;
  }
  const field = SCOPE_FIELD_BY_TYPE[scopeFilter.type];
  return field ? c[field] === scopeFilter.id : true;
}

function startOfDay(ts) {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Group active (non-archived) conversations into Today / Yesterday / Past 7
 * days / Older buckets based on updated_at.
 */
export function groupConversations(list, now = Date.now()) {
  const today = startOfDay(now);
  const yesterday = today - MS_PER_DAY;
  const sevenDaysAgo = today - 7 * MS_PER_DAY;

  const groups = {
    today: [],
    yesterday: [],
    pastWeek: [],
    older: [],
  };

  for (const c of list || []) {
    const t = new Date(c.updated_at || c.created_at || 0).getTime();
    if (t >= today) groups.today.push(c);
    else if (t >= yesterday) groups.yesterday.push(c);
    else if (t >= sevenDaysAgo) groups.pastWeek.push(c);
    else groups.older.push(c);
  }

  return groups;
}

/**
 * @param {object} [options]
 * @param {number} [options.limit]         — max active conversations to fetch
 * @param {number} [options.archivedLimit] — max archived conversations to fetch
 */
export function useChatList({ limit = 100, archivedLimit = 50 } = {}) {
  const [conversations, setConversations] = useState([]);
  const [archivedConversations, setArchivedConversations] = useState([]);
  const [scopeFilter, setScopeFilter] = useState(null); // null=all | '__unassigned__' | { type, id }
  const [archivedExpanded, setArchivedExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // 2026-04-20 — 10s hard timeout. User reported "side menu stuck
        // loading" on mobile; the Supabase client occasionally hangs a
        // query on a stale JWT without ever rejecting. Surfacing an
        // error beats spinning forever.
        const queryPromise = Promise.all([
          supabase
            .from('chat_conversations')
            .select('id, title, project_id, deal_id, task_id, assigned_person_id, archived_at, created_at, updated_at')
            .is('archived_at', null)
            .order('updated_at', { ascending: false })
            .limit(limit),
          supabase
            .from('chat_conversations')
            .select('id, title, project_id, deal_id, task_id, assigned_person_id, archived_at, created_at, updated_at')
            .not('archived_at', 'is', null)
            .order('archived_at', { ascending: false })
            .limit(archivedLimit),
        ]);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Chat list fetch timed out (10s) — check network or refresh.')), 10_000)
        );
        const [activeResp, archivedResp] = await Promise.race([queryPromise, timeoutPromise]);

        if (cancelled) return;
        if (activeResp.error) throw new Error(activeResp.error.message);
        if (archivedResp.error) throw new Error(archivedResp.error.message);

        setConversations(activeResp.data || []);
        setArchivedConversations(archivedResp.data || []);
      } catch (err) {
        if (!cancelled) {
          console.error('[useChatList] fetch failed:', err?.message);
          setError(err.message || String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshKey, limit, archivedLimit]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = () => refresh();
    window.addEventListener('chat-conversation-updated', handler);
    return () => window.removeEventListener('chat-conversation-updated', handler);
  }, [refresh]);

  const filteredActive = useMemo(
    () => conversations.filter(c => matchesScope(c, scopeFilter)),
    [conversations, scopeFilter]
  );

  const filteredArchived = useMemo(
    () => archivedConversations.filter(c => matchesScope(c, scopeFilter)),
    [archivedConversations, scopeFilter]
  );

  const groups = useMemo(() => groupConversations(filteredActive), [filteredActive]);

  // Back-compat: AgentShell → sidebar sync passes a project_id. Keep the
  // project-only setter/getter working by mapping to/from the scope descriptor.
  const projectFilter = scopeFilter === '__unassigned__'
    ? '__unassigned__'
    : (scopeFilter && scopeFilter.type === 'project' ? scopeFilter.id : null);
  const setProjectFilter = useCallback((id) => {
    setScopeFilter(id == null ? null : id === '__unassigned__' ? '__unassigned__' : { type: 'project', id });
  }, []);

  return {
    conversations: filteredActive,
    archivedConversations: filteredArchived,
    groups,
    scopeFilter,
    setScopeFilter,
    projectFilter,
    setProjectFilter,
    archivedExpanded,
    setArchivedExpanded,
    loading,
    error,
    refresh,
    allConversations: conversations,
    allArchivedConversations: archivedConversations,
  };
}

export { groupConversations as _groupConversations };
